/**
 * Hearing-aid replacement requests, as /api/v1 answers them (ruling, Isaiah Cotto 2026-10-21: "Any office member can
 * file a replacement, and replacements can also have their own tab in UMED").
 *
 * A request is filed from a resident (GET/POST /replacement-requests/residents/:id), withdrawn by whoever filed it
 * (POST /replacement-requests/:id/cancel), listed in UMED (GET /umed/replacements) and placed with the facility's
 * Patient Advocate from the Replacements desk (POST /manifest-intake/replacements/:patientId/place).
 *
 * The server decides who may file, choose the payment path, withdraw or place; this file names shapes and sends the
 * act. A refusal is `{ success:false, code, message }` and is shown as returned. A thrown transport error (the server
 * was never reached) comes back with no status and no code.
 */
import { api } from '../../services/api';
import type { BoardTabDef } from '../board';

export interface ReplacementResult<T> { success: boolean; data?: T; error?: string; code?: string; message?: string; detail?: unknown; status?: number; already?: string; missing?: string[] }

export type ReplacementSide = 'LEFT' | 'RIGHT' | 'BOTH';
export type ReplacementReason = 'LOST' | 'DAMAGED' | 'NOT_WORKING' | 'RECHARGEABLE_UPGRADE' | 'WARRANTY_REPLACEMENT' | 'OTHER';
export type ReplacementState = 'OPEN' | 'PLACED' | 'CANCELLED';
export type ReplacementTab = 'open' | 'placed' | 'cancelled' | 'all';

export const SIDE_OPTIONS: { value: ReplacementSide; label: string }[] = [
  { value: 'LEFT', label: 'Left' },
  { value: 'RIGHT', label: 'Right' },
  { value: 'BOTH', label: 'Both' },
];
export const REASON_OPTIONS: { value: ReplacementReason; label: string }[] = [
  { value: 'LOST', label: 'Lost' },
  { value: 'DAMAGED', label: 'Damaged' },
  { value: 'NOT_WORKING', label: 'Not working' },
  { value: 'RECHARGEABLE_UPGRADE', label: 'Rechargeable upgrade' },
  { value: 'WARRANTY_REPLACEMENT', label: 'Warranty replacement' },
  { value: 'OTHER', label: 'Other (say what in the note)' },
];
/** How it is paid for: shown to those who hold place rights only. The first is the default; the office decides. */
export const PATH_OPTIONS: { value: string; label: string }[] = [
  { value: 'REVIEW_REQUIRED', label: 'Review required — the office decides' },
  { value: 'WARRANTY', label: 'Warranty' },
  { value: 'REPLACEMENT_CHARGE', label: 'Replacement charge' },
  { value: 'NEW_UMED', label: 'New UMED' },
  { value: 'PRIVATE_PAY', label: 'Private pay' },
  { value: 'SPENDDOWN_PLUS_UMED', label: 'Spenddown plus UMED' },
];
export const NOTE_MAX = 1000;

export interface ReplacementStatus {
  patient_id: string;
  may_file: boolean;
  refusal: { code: string; message: string } | null;
  may_choose_path: boolean;
  building: { id: string; name: string | null } | null;
  open: { id: string; side: ReplacementSide; reason_code: string; reason_words: string; requested_at: string; requested_by: string | null; mine: boolean } | null;
  placed_recently: { id: string; placed_at: string; pa: string | null } | null;
  window_days: number;
  desk_href: string;
}

export interface ReplacementFileBody {
  side: ReplacementSide;
  reason_code: ReplacementReason;
  note: string | null;
  idempotency_key: string;
  financial_path?: string;
  requested_amount_cents?: number;
}
export interface ReplacementFiled { id: string; state: 'OPEN'; replayed: boolean; requested_at: string }
export interface ReplacementCancelled { id: string; state: 'CANCELLED' }

export interface ReplacementChecks {
  warranty: { result: 'PASS' | 'FAIL'; warranty_expires_on: string | null; checked_by: string | null } | null;
  paid_in_full: { result: 'PASS' | 'FAIL'; checked_by: string | null } | null;
}
export interface ReplacementRow {
  id: string; patient_id: string; resident: string | null;
  facility: { id: string; name: string | null } | null;
  side: ReplacementSide; reason_code: string; reason_words: string;
  financial_path: string; requested_amount_cents: number | null; note: string | null;
  state: ReplacementState; state_words: 'Requested' | 'Placed to PA' | 'Cancelled';
  requested_by: string | null; requested_by_me: boolean; requested_at: string;
  placed_at: string | null; placed_by: string | null;
  pa_assigned: string | null; goes_to: string | null;
  cancelled_at: string | null; cancelled_by: string | null; cancel_reason: string | null;
  checks: ReplacementChecks | null;
  ready: boolean | null; missing: string[];
  can_cancel: boolean; can_place: boolean;
}
export interface ReplacementsPage {
  state: ReplacementTab;
  tabs: BoardTabDef[];
  items: ReplacementRow[];
  total: number; page: number; limit: number; window_days: number;
  sort: string | null; dir: 'asc' | 'desc'; sorts: string[];
  desk_href: string;
  rule: string;
}
export interface ReplacementsQuery { state?: string; q?: string; page?: number; limit?: number }
export interface ReplacementPlaced { requestId?: string; caseId?: string; advocate?: unknown; facility?: unknown }

export const REPLACEMENT_TABS: readonly ReplacementTab[] = ['open', 'placed', 'cancelled', 'all'];

export function replacementsQueryString(p: ReplacementsQuery): string {
  const qp = new URLSearchParams();
  const put = (k: string, v: string | number | null | undefined) => { if (v !== null && v !== undefined && v !== '') qp.set(k, String(v)); };
  put('state', p.state || 'open');
  put('q', p.q);
  put('page', p.page && p.page > 1 ? p.page : null);
  put('limit', p.limit);
  const s = qp.toString();
  return s ? `?${s}` : '';
}

/** A thrown transport error is "the server was never reached": no status, no code. */
async function call<T>(run: () => Promise<any>): Promise<ReplacementResult<T>> {
  try {
    const r = await run();
    return (r || { success: false, error: 'No answer from the server.' }) as ReplacementResult<T>;
  } catch {
    return { success: false, error: 'Could not reach the server.' };
  }
}

/** One key per attempt; a retry after a lost answer reuses it. The server accepts 8-64 characters of [A-Za-z0-9_-]. */
export const newReplacementKey = (): string => {
  const c = typeof window !== 'undefined' ? window.crypto : undefined;
  const raw = c && typeof c.randomUUID === 'function' ? c.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  return `of-${raw}`.replace(/[^A-Za-z0-9_-]/g, '-').slice(0, 64);
};

/** Dollars typed by a person to whole cents; null when blank or not a money amount. */
export function dollarsToCents(text: string): number | null {
  const t = text.trim().replace(/^\$/, '').replace(/,/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  return Math.round(parseFloat(t) * 100);
}

export const replacementApi = {
  status: (patientId: string) => call<ReplacementStatus>(() => api.get(`/replacement-requests/residents/${encodeURIComponent(patientId)}`)),
  file: (patientId: string, body: ReplacementFileBody) =>
    call<ReplacementFiled>(() => api.post(`/replacement-requests/residents/${encodeURIComponent(patientId)}`, body)),
  cancel: (requestId: string, reason: string) =>
    call<ReplacementCancelled>(() => api.post(`/replacement-requests/${encodeURIComponent(requestId)}/cancel`, { reason })),
  list: (p: ReplacementsQuery = {}) => call<ReplacementsPage>(() => api.get(`/umed/replacements${replacementsQueryString(p)}`)),
  place: (patientId: string, note?: string) =>
    call<ReplacementPlaced>(() => api.post(`/manifest-intake/replacements/${encodeURIComponent(patientId)}/place`, note ? { note } : {})),
};

const ET_DAY = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric' });
/** A moment as the New York calendar day it fell on ("Oct 21, 2026"); a stamp is never read as a UTC date. */
export function etDay(value: string | null | undefined): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : ET_DAY.format(d);
}
